import { useMemo, useState, type ReactNode } from 'react';
import {
  Table,
  Input,
  Select,
  Tag,
  Space,
  Typography,
  Button,
  Spin,
  Tabs,
  Modal,
  Timeline,
  Popconfirm,
  message,
  Alert,
  Card,
  Tooltip,
  Switch,
} from 'antd';
import { SearchOutlined, ReloadOutlined, WarningOutlined, SwapRightOutlined, CheckCircleOutlined, DeleteOutlined, PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  getGenericMappings,
  createGenericMapping,
  updateGenericMapping,
  deleteGenericMapping,
  getGenericProjectIdMappings,
  createGenericProjectIdMapping,
  getProjectTypes,
  updateProjectAttributeChangeLogActionTaken,
} from '../../api/project_type';
import {
  getRegistryMethodologyPlatforms,
  createRegistryMethodologyPlatform,
} from '../../api/registry_methodology';

const { Title, Text } = Typography;

type StatusType = 'mapped' | 'needs-mapping';

function getStatusFromCalyxType(calyxType: string | null | undefined): StatusType {
  return calyxType ? 'mapped' : 'needs-mapping';
}

function getMethodologyRowStatus(record: any): StatusType {
  if (record.multi_project_type) return 'mapped';
  return getStatusFromCalyxType(record.calyx_project_type?.display_name ?? null);
}

function renderStatusTag(status: StatusType) {
  if (status === 'mapped') return <Tag color="green">Mapped</Tag>;
  return <Tag color="red">Needs Mapping</Tag>;
}

const STAT_CARD_SOFT_BG: Record<string, string> = {
  '#8c8c8c': '#f4f4f4',
  '#52c41a': '#f0fbe8',
  '#ff4d4f': '#fff1f1',
  '#1677ff': '#eef5ff',
  '#fa8c16': '#fff7e8',
};

function StatCards({
  stats,
}: {
  stats: {
    label: string;
    value: number;
    color: string;
    active?: boolean;
    onClick?: () => void;
    icon?: ReactNode;
  }[];
}) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
      {stats.map((stat) => (
        <div
          key={stat.label}
          onClick={stat.onClick}
          style={{
            flex: '1 1 128px',
            minWidth: 128,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            background: stat.active ? STAT_CARD_SOFT_BG[stat.color] || '#f5f5f5' : '#fff',
            borderTop: stat.active ? `1px solid ${stat.color}` : '1px solid #f0f0f0',
            borderRight: stat.active ? `1px solid ${stat.color}` : '1px solid #f0f0f0',
            borderBottom: stat.active ? `1px solid ${stat.color}` : '1px solid #f0f0f0',
            borderLeft: `3px solid ${stat.color}`,
            borderRadius: 8,
            padding: '7px 12px',
            cursor: stat.onClick ? 'pointer' : 'default',
            transition: 'background 0.15s, border-color 0.15s',
          }}
        >
          <Text type="secondary" style={{ fontSize: 11.5, display: 'flex', alignItems: 'center', gap: 4 }}>
            {stat.icon && <span style={{ color: stat.color }}>{stat.icon}</span>}
            {stat.label}
          </Text>
          <Text strong style={{ fontSize: 17, fontVariantNumeric: 'tabular-nums' }}>
            {stat.value}
          </Text>
        </div>
      ))}
    </div>
  );
}

// ─── Registry Type Level Tab ───────────────────────────────────────────

async function fetchGenericMappingsData() {
  const data = await getGenericMappings();
  const rows = Array.isArray(data) ? data : data.response || [];
  return rows as any[];
}

async function fetchRegistryMethodologyPlatformsData() {
  const data = await getRegistryMethodologyPlatforms();
  const rows = Array.isArray(data) ? data : data.response || [];
  return rows as any[];
}

/**
 * Methodology names are long and often differ only in a trailing version or suffix, so the
 * default single-line, ellipsized option hides exactly the part that tells them apart.
 * Wrap each option onto as many lines as it needs. Antd's virtual list assumes a fixed row
 * height, so wrapped options only lay out correctly with virtual scrolling off (the list is
 * a few hundred entries at most).
 */
const wrappedOptionSelectProps = {
  virtual: false,
  listHeight: 360,
  optionRender: (option: { label?: ReactNode }) => (
    <div style={{ whiteSpace: 'normal', lineHeight: 1.4, padding: '2px 0' }}>{option.label}</div>
  ),
};

function RegistryTypeLevelTab({
  calyxOptions,
}: {
  calyxOptions: { value: string; label: string }[];
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | StatusType | 'new'>('all');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createRegistryMethodology, setCreateRegistryMethodology] = useState<string | null>(null);
  const [createMappingHarmonizedMethodology, setCreateMappingHarmonizedMethodology] = useState<string | null>(null);
  const [createCalyxProjectType, setCreateCalyxProjectType] = useState<string | null>(null);
  const [createHarmonizedModalOpen, setCreateHarmonizedModalOpen] = useState(false);
  const [createHarmonizedDisplayName, setCreateHarmonizedDisplayName] = useState('');

  const {
    data: rows = [],
    isLoading: mappingsLoading,
    isFetching: mappingsFetching,
  } = useQuery({
    queryKey: ['genericProjectTypeMappings'],
    queryFn: fetchGenericMappingsData,
  });

  const {
    data: harmonizedMethodologies = [],
    isLoading: harmonizedLoading,
    isFetching: harmonizedFetching,
  } = useQuery({
    queryKey: ['registryMethodologyPlatforms'],
    queryFn: fetchRegistryMethodologyPlatformsData,
  });

  const harmonizedOptions = useMemo(
    () =>
      (harmonizedMethodologies as any[]).map((m) => ({
        value: m.display_name,
        label: m.display_name,
      })),
    [harmonizedMethodologies],
  );

  const { mutate: updateMapping, isLoading: isUpdating } = useMutation(
    ({
      record,
      calyxProjectType,
      harmonizedMethodology,
      multiProjectType,
    }: {
      record: any;
      calyxProjectType: string | null;
      harmonizedMethodology: string | null;
      multiProjectType: boolean;
    }) =>
      updateGenericMapping({
        registry_methodology: record.registry_methodology_platform?.display_name,
        calyx_project_type: calyxProjectType,
        harmonized_methodology: harmonizedMethodology,
        multi_project_type: multiProjectType,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['genericProjectTypeMappings']);
        queryClient.invalidateQueries(['genericProjectIdMappings']);
        message.success('Mapping updated');
      },
      onError: () => {
        message.error('Failed to update mapping. Please try again.');
      },
    },
  );

  const { mutate: createHarmonizedMethodology, isLoading: isCreatingHarmonized } = useMutation(
    () => createRegistryMethodologyPlatform({ display_name: createHarmonizedDisplayName.trim() }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['registryMethodologyPlatforms']);
        message.success('Harmonized methodology created');
        setCreateHarmonizedModalOpen(false);
        setCreateHarmonizedDisplayName('');
      },
      onError: (err: any) => {
        message.error(err?.message || 'Failed to create harmonized methodology. Please try again.');
      },
    },
  );

  const { mutate: deleteMapping, isLoading: isDeleting } = useMutation(
    (id: string) => deleteGenericMapping(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['genericProjectTypeMappings']);
        message.success('Mapping deleted');
      },
      onError: () => {
        message.error('Failed to delete mapping. Please try again.');
      },
    },
  );

  const { mutate: createMapping, isLoading: isCreating } = useMutation(
    () =>
      createGenericMapping({
        registry_methodology: createRegistryMethodology,
        harmonized_methodology: createMappingHarmonizedMethodology,
        calyx_project_type: createCalyxProjectType,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['genericProjectTypeMappings']);
        message.success('Mapping created');
        setCreateModalOpen(false);
        setCreateRegistryMethodology(null);
        setCreateMappingHarmonizedMethodology(null);
        setCreateCalyxProjectType(null);
      },
      onError: (err: any) => {
        message.error(err?.message || 'Failed to create mapping. Please try again.');
      },
    },
  );

  const loading =
    mappingsLoading || mappingsFetching || harmonizedLoading || harmonizedFetching || isUpdating || isDeleting;

  const handleCalyxTypeChange = (record: any, value: string | null) => {
    updateMapping({
      record,
      calyxProjectType: value ?? null,
      harmonizedMethodology: record.harmonized_methodology_platform?.display_name ?? null,
      multiProjectType: !!record.multi_project_type,
    });
  };

  const handleHarmonizedMethodologyChange = (record: any, value: string | null) => {
    updateMapping({
      record,
      calyxProjectType: record.calyx_project_type?.display_name ?? null,
      harmonizedMethodology: value ?? null,
      multiProjectType: !!record.multi_project_type,
    });
  };

  const handleMultiProjectTypeChange = (record: any, checked: boolean) => {
    updateMapping({
      record,
      calyxProjectType: record.calyx_project_type?.display_name ?? null,
      harmonizedMethodology: record.harmonized_methodology_platform?.display_name ?? null,
      multiProjectType: checked,
    });
  };

  const filteredRows = useMemo(() => {
    const text = search.toLowerCase().trim();
    return (rows as any[]).filter((row) => {
      const projectTypeName = row.calyx_project_type?.display_name ?? null;
      const status = getMethodologyRowStatus(row);
      if (statusFilter === 'new') {
        if (!(status === 'needs-mapping' && isRecentlyCreated(row.created_at))) return false;
      } else if (statusFilter !== 'all' && status !== statusFilter) {
        return false;
      }
      if (!text) return true;
      const haystack = [
        row.harmonized_methodology_platform?.display_name || '',
        row.registry_methodology_platform?.display_name || '',
        projectTypeName || '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(text);
    });
  }, [rows, search, statusFilter]);

  const columns: any[] = [
    {
      title: 'Methodology (Berkeley + Registry)',
      key: 'registry_methodology_platform',
      width: 260,
      sorter: (a: any, b: any) =>
        (a.registry_methodology_platform?.display_name || '').localeCompare(
          b.registry_methodology_platform?.display_name || '',
        ),
      render: (_: any, record: any) => {
        const text = record.registry_methodology_platform?.display_name || '—';
        const isNew = getMethodologyRowStatus(record) === 'needs-mapping' && isRecentlyCreated(record.created_at);
        return (
          <Tooltip title={text} placement="topLeft">
            <Space size={4} style={{ maxWidth: '100%' }}>
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                  maxWidth: isNew ? 170 : 230,
                }}
              >
                {text}
              </span>
              {isNew && <Tag color="blue">New</Tag>}
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Harmonized Methodology',
      key: 'harmonized_methodology_platform',
      width: 300,
      sorter: (a: any, b: any) =>
        (a.harmonized_methodology_platform?.display_name || '').localeCompare(
          b.harmonized_methodology_platform?.display_name || '',
        ),
      render: (_: any, record: any) => {
        const value = record.harmonized_methodology_platform?.display_name ?? null;
        const options =
          value && !harmonizedOptions.some((o) => o.value === value)
            ? [{ value, label: value }, ...harmonizedOptions]
            : harmonizedOptions;
        return (
          <Tooltip title={value || ''} placement="topLeft">
            <Select
              allowClear
              disabled={!!record.multi_project_type}
              placeholder="Select Harmonized Methodology"
              value={value || undefined}
              options={options}
              onChange={(val) => handleHarmonizedMethodologyChange(record, val ?? null)}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'Calyx Project Type',
      key: 'calyx_project_type',
      width: 300,
      sorter: (a: any, b: any) =>
        (a.calyx_project_type?.display_name || '').localeCompare(b.calyx_project_type?.display_name || ''),
      render: (_: any, record: any) => {
        const value = record.calyx_project_type?.display_name ?? null;
        const options =
          value && !calyxOptions.some((o: any) => o.value === value)
            ? [{ value, label: value }, ...calyxOptions]
            : calyxOptions;
        return (
          <Select
            allowClear
            disabled={!!record.multi_project_type}
            placeholder="Select Calyx Project Type"
            value={value || undefined}
            options={options}
            onChange={(val) => handleCalyxTypeChange(record, val ?? null)}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
          />
        );
      },
    },
    {
      title: (
        <Space size={4}>
          Multiple Project Types
          <Tooltip title="When enabled, this methodology is no longer mapped at the methodology level, entries are created in the Project ID Override tab so each project can be mapped individually.">
            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
          </Tooltip>
        </Space>
      ),
      key: 'multi_project_type',
      width: 170,
      sorter: (a: any, b: any) => Number(!!a.multi_project_type) - Number(!!b.multi_project_type),
      render: (_: any, record: any) => (
        <Switch
          checked={!!record.multi_project_type}
          onChange={(checked) => handleMultiProjectTypeChange(record, checked)}
        />
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      sorter: (a: any, b: any) => getMethodologyRowStatus(a).localeCompare(getMethodologyRowStatus(b)),
      render: (_: any, record: any) => {
        const status = getMethodologyRowStatus(record);
        return renderStatusTag(status);
      },
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: any, record: any) => (
        <Popconfirm
          title="Delete this mapping?"
          okText="Delete"
          okType="danger"
          onConfirm={() => deleteMapping(record.id)}
          disabled={!!record.multi_project_type}
        >
          <Button
            size="small"
            type="text"
            danger
            icon={<DeleteOutlined />}
            disabled={!!record.multi_project_type}
          />
        </Popconfirm>
      ),
    },
  ];

  const needsMappingCount = (rows as any[]).filter(
    (r) => getMethodologyRowStatus(r) === 'needs-mapping',
  ).length;
  const mappedCount = (rows as any[]).length - needsMappingCount;
  const newCount = (rows as any[]).filter(
    (r) => getMethodologyRowStatus(r) === 'needs-mapping' && isRecentlyCreated(r.created_at),
  ).length;

  return (
    <Spin spinning={loading} tip="Loading generic mappings…">
      <StatCards
        stats={[
          {
            label: 'Total Methodologies',
            value: (rows as any[]).length,
            color: '#8c8c8c',
            active: statusFilter === 'all',
            onClick: () => setStatusFilter('all'),
          },
          {
            label: 'Mapped',
            value: mappedCount,
            color: '#52c41a',
            active: statusFilter === 'mapped',
            onClick: () => setStatusFilter('mapped'),
          },
          {
            label: 'Needs Mapping',
            value: needsMappingCount,
            color: '#ff4d4f',
            active: statusFilter === 'needs-mapping',
            onClick: () => setStatusFilter('needs-mapping'),
          },
          {
            label: 'New',
            value: newCount,
            color: '#1677ff',
            active: statusFilter === 'new',
            onClick: () => setStatusFilter('new'),
          },
        ]}
      />
      <div
        style={{
          margin: '0 0 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search Methodology, Calyx Project Type…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 360 }}
        />
        <Space wrap>
          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => queryClient.invalidateQueries(['genericProjectTypeMappings'])}
            />
          </Tooltip>
          <Button
            icon={<PlusOutlined />}
            onClick={() => setCreateHarmonizedModalOpen(true)}
          >
            Add Harmonized Methodology
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalOpen(true)}
          >
            Add Mapping
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={filteredRows}
        rowKey="id"
        size="middle"
        tableLayout="fixed"
        showSorterTooltip={false}
        onRow={(record) => {
          if (record.multi_project_type) return { style: { background: '#fafafa' } };
          if (getMethodologyRowStatus(record) === 'needs-mapping' && isRecentlyCreated(record.created_at)) {
            return { style: { background: '#e6f4ff' } };
          }
          return {};
        }}
        pagination={{ defaultPageSize: 50, showSizeChanger: true }}
      />
      <Modal
        title="Add Harmonized Methodology"
        open={createHarmonizedModalOpen}
        onCancel={() => {
          setCreateHarmonizedModalOpen(false);
          setCreateHarmonizedDisplayName('');
        }}
        onOk={() => createHarmonizedMethodology()}
        okText="Create"
        confirmLoading={isCreatingHarmonized}
        okButtonProps={{ disabled: !createHarmonizedDisplayName.trim() }}
      >
        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
          <div>
            <Text strong>Harmonized Methodology Name</Text>
            <Text type="danger"> *</Text>
            <Input
              style={{ marginTop: 4 }}
              placeholder="Enter harmonized methodology display name"
              value={createHarmonizedDisplayName}
              onChange={(e) => setCreateHarmonizedDisplayName(e.target.value)}
            />
          </div>
          <Alert
            type="info"
            showIcon
            message="If you want to assign multiple harmonized methodologies to a single Methodology (Berkeley + Registry), enter them on one line separated by semicolons (;)."
          />
        </Space>
      </Modal>
      <Modal
        title="Add Generic Mapping"
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          setCreateRegistryMethodology(null);
          setCreateMappingHarmonizedMethodology(null);
          setCreateCalyxProjectType(null);
        }}
        onOk={() => createMapping()}
        okText="Create"
        confirmLoading={isCreating}
        okButtonProps={{
          disabled: !createRegistryMethodology || !createMappingHarmonizedMethodology || !createCalyxProjectType,
        }}
        width={800}
      >
        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
          <div>
            <Text strong>Registry Methodology</Text>
            <Text type="danger"> *</Text>
            <Select
              allowClear
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select Registry Methodology"
              value={createRegistryMethodology || undefined}
              options={harmonizedOptions}
              onChange={(val) => setCreateRegistryMethodology(val ?? null)}
              showSearch
              optionFilterProp="label"
              {...wrappedOptionSelectProps}
            />
          </div>
          <div>
            <Text strong>Harmonized Methodology</Text>
            <Text type="danger"> *</Text>
            <Select
              allowClear
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select Harmonized Methodology"
              value={createMappingHarmonizedMethodology || undefined}
              options={harmonizedOptions}
              onChange={(val) => setCreateMappingHarmonizedMethodology(val ?? null)}
              showSearch
              optionFilterProp="label"
              {...wrappedOptionSelectProps}
            />
          </div>
          <div>
            <Text strong>Calyx Project Type</Text>
            <Text type="danger"> *</Text>
            <Select
              allowClear
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select Calyx Project Type"
              value={createCalyxProjectType || undefined}
              options={calyxOptions}
              onChange={(val) => setCreateCalyxProjectType(val ?? null)}
              showSearch
              optionFilterProp="label"
            />
          </div>
        </Space>
      </Modal>
    </Spin>
  );
}

// ─── Project ID Level Tab ──────────────────────────────────────────────

const NEW_ENTRY_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

function isRecentlyCreated(createdAt: string | null | undefined) {
  if (!createdAt) return false;
  const createdTime = new Date(createdAt).getTime();
  if (Number.isNaN(createdTime)) return false;
  return Date.now() - createdTime <= NEW_ENTRY_WINDOW_MS;
}

async function fetchGenericProjectIdMappingsData() {
  const data = await getGenericProjectIdMappings();
  const rows = Array.isArray(data) ? data : data.response || [];
  return rows.map((row: any, index: number) => ({
    ...row,
    id: row.id ?? `${row.registry || 'REG'}-${row.project_id || 'PID'}-${index}`,
  }));
}

async function updateGenericProjectIdCalyxProjectType(
  record: any,
  calyxType: string | null,
  calyxMethodology: string | null,
) {
  const payload = {
    project_id: record.project_id,
    ghg_standard_code: record.registry,
    project_id_mapped_project_type: calyxType,
    project_id_mapped_methodology: calyxMethodology,
  };
  await createGenericProjectIdMapping(payload);
  return {
    ...record,
    project_id_mapped_project_type: calyxType,
    project_id_mapped_methodology: calyxMethodology,
  };
}

function ChangeLogsModal({
  open,
  changeLogs,
  onClose,
  onActionTakenAll,
  actionLoading,
}: {
  open: boolean;
  changeLogs: any[];
  onClose: () => void;
  onActionTakenAll: () => void;
  actionLoading: boolean;
}) {
  return (
    <Modal
      title={
        <Space>
          <WarningOutlined style={{ color: '#fa8c16' }} />
          <span>Registry Changes Detected</span>
          <Tag color="orange">{changeLogs.length}</Tag>
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {changeLogs.length} change{changeLogs.length !== 1 ? 's' : ''} detected
          </Text>
          <Space>
            <Button onClick={onClose}>Close</Button>
            {changeLogs.length > 0 && (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={actionLoading}
                onClick={onActionTakenAll}
              >
                Resolve All
              </Button>
            )}
          </Space>
        </div>
      }
      width={560}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>
       Please resolve all items only after updating the mapping. If not completed, you can click Close.
      </Text>
      <Timeline
        items={changeLogs.map((log: any) => ({
          key: log.id,
          color: log.field_name === 'project_type' ? 'blue' : 'green',
          children: (
            <div
              style={{
                padding: '8px 12px',
                background: '#fafafa',
                borderRadius: 6,
                border: '1px solid #f0f0f0',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <Tag color={log.field_name === 'project_type' ? 'blue' : 'green'}>
                  {log.field_name === 'project_type' ? 'Project Type' : 'Methodology'}
                </Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {new Date(log.detected_at).toLocaleString()}
                </Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text delete type="secondary" style={{ flex: 1 }}>
                  {log.old_value}
                </Text>
                <SwapRightOutlined style={{ color: '#8c8c8c', flexShrink: 0 }} />
                <Text strong style={{ flex: 1 }}>
                  {log.new_value}
                </Text>
              </div>
            </div>
          ),
        }))}
      />
    </Modal>
  );
}

function ProjectIdLevelTab({
  calyxOptions,
}: {
  calyxOptions: { value: string; label: string }[];
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | StatusType | 'new' | 'changes-detected'>('all');
  const [changeLogModalRecord, setChangeLogModalRecord] = useState<any>(null);

  const {
    data: rows = [],
    isLoading: mappingsLoading,
    isFetching: mappingsFetching,
  } = useQuery({
    queryKey: ['genericProjectIdMappings'],
    queryFn: fetchGenericProjectIdMappingsData,
  });

  const {
    data: harmonizedMethodologies = [],
    isLoading: harmonizedLoading,
    isFetching: harmonizedFetching,
  } = useQuery({
    queryKey: ['registryMethodologyPlatforms'],
    queryFn: fetchRegistryMethodologyPlatformsData,
  });

  const harmonizedMethodologyOptions = useMemo(
    () =>
      (harmonizedMethodologies as any[]).map((m) => ({
        value: m.display_name,
        label: m.display_name,
      })),
    [harmonizedMethodologies],
  );

  const { mutate: markAllActionTaken, isLoading: actionLoading } = useMutation(
    (ids: string[]) =>
      Promise.all(ids.map((id) => updateProjectAttributeChangeLogActionTaken(id, true))),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['genericProjectIdMappings']);
        message.success('All change logs resolved');
        setChangeLogModalRecord(null);
      },
      onError: () => {
        message.error('Failed to resolve change logs. Please try again.');
      },
    },
  );

  const { mutate: updateGenericProjectIdMapping, isLoading: isUpdating } = useMutation(
    ({
      record,
      calyxType,
      calyxMethodology,
    }: {
      record: any;
      calyxType: string | null;
      calyxMethodology: string | null;
    }) => updateGenericProjectIdCalyxProjectType(record, calyxType, calyxMethodology),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['genericProjectIdMappings']);
        message.success('Generic project ID mapping updated');
      },
      onError: () => {
        message.error('Failed to update generic project ID mapping. Please try again.');
      },
    },
  );

  const loading =
    mappingsLoading || mappingsFetching || harmonizedLoading || harmonizedFetching || isUpdating;

  function getProjectFinalCalyxType(row: any) {
    return row.project_id_mapped_project_type || null;
  }

  const filteredRows = useMemo(() => {
    const text = search.toLowerCase().trim();
    return (rows as any[]).filter((row) => {
      const finalType = getProjectFinalCalyxType(row);
      const status = getStatusFromCalyxType(finalType);
      const hasChangesDetected = Array.isArray(row.change_logs) && row.change_logs.length > 0;
      if (statusFilter === 'new') {
        if (!(status === 'needs-mapping' && isRecentlyCreated(row.created_at))) return false;
      } else if (statusFilter === 'changes-detected') {
        if (!hasChangesDetected) return false;
      } else if (statusFilter !== 'all' && status !== statusFilter) {
        return false;
      }
      if (!text) return true;
      const haystack = [
        row.registry,
        row.project_id,
        row.project_id_mapped_project_type || '',
        row.project_id_mapped_methodology || '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(text);
    });
  }, [rows, search, statusFilter]);

  const handleCalyxTypeChange = (record: any, value: string | null) => {
    updateGenericProjectIdMapping({
      record,
      calyxType: value ?? null,
      calyxMethodology: record.project_id_mapped_methodology ?? null,
    });
  };

  const handleCalyxMethodologyChange = (record: any, value: string | null) => {
    updateGenericProjectIdMapping({
      record,
      calyxType: record.project_id_mapped_project_type ?? null,
      calyxMethodology: value ?? null,
    });
  };

  const columns: any[] = [
    {
      title: 'Project ID',
      key: 'project_id',
      width: 160,
      sorter: (a: any, b: any) => (a.project_id || '').localeCompare(b.project_id || ''),
      render: (_: any, record: any) => {
        const isUnmapped = getStatusFromCalyxType(getProjectFinalCalyxType(record)) === 'needs-mapping';
        return (
          <Space size={4}>
            {record.project_id}
            {isUnmapped && isRecentlyCreated(record.created_at) && <Tag color="blue">New</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Registry',
      dataIndex: 'registry',
      key: 'registry',
      width: 90,
      sorter: (a: any, b: any) => (a.registry || '').localeCompare(b.registry || ''),
    },
    {
      title: '',
      key: 'change_logs',
      width: 50,
      render: (_: any, record: any) => {
        const logs = record.change_logs;
        if (!logs || logs.length === 0) return null;
        return (
          <Button
            size="small"
            type="text"
            icon={<WarningOutlined />}
            style={{ color: '#fa8c16' }}
            onClick={() => setChangeLogModalRecord(record)}
          />
        );
      },
    },
    {
      title: 'Calyx Project Type',
      key: 'calyx_project_type',
      width: 260,
      sorter: (a: any, b: any) =>
        (a.project_id_mapped_project_type || '').localeCompare(b.project_id_mapped_project_type || ''),
      render: (_: any, record: any) => (
        <Select
          allowClear
          placeholder="Select Calyx Project Type"
          value={record.project_id_mapped_project_type || undefined}
          options={calyxOptions}
          onChange={(val) => handleCalyxTypeChange(record, val ?? null)}
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="label"
        />
      ),
    },
    {
      title: 'Harmonized Registry Methodology',
      dataIndex: 'project_id_mapped_methodology',
      key: 'project_id_mapped_methodology',
      width: 260,
      sorter: (a: any, b: any) =>
        (a.project_id_mapped_methodology || '').localeCompare(b.project_id_mapped_methodology || ''),
      render: (value: string | null, record: any) => {
        const options =
          value && !harmonizedMethodologyOptions.some((o) => o.value === value)
            ? [{ value, label: value }, ...harmonizedMethodologyOptions]
            : harmonizedMethodologyOptions;
        return (
          <Select
            allowClear
            placeholder="Select Harmonized Registry Methodology"
            value={value || undefined}
            options={options}
            onChange={(val) => handleCalyxMethodologyChange(record, val ?? null)}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
          />
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      sorter: (a: any, b: any) =>
        getStatusFromCalyxType(getProjectFinalCalyxType(a)).localeCompare(
          getStatusFromCalyxType(getProjectFinalCalyxType(b)),
        ),
      render: (_: any, record: any) => {
        const finalType = getProjectFinalCalyxType(record);
        const status = getStatusFromCalyxType(finalType);
        return renderStatusTag(status);
      },
    },
  ];

  const needsMappingCount = (rows as any[]).filter((r) => {
    const finalType = getProjectFinalCalyxType(r);
    return getStatusFromCalyxType(finalType) === 'needs-mapping';
  }).length;
  const mappedCount = (rows as any[]).length - needsMappingCount;
  const newCount = (rows as any[]).filter((r) => {
    const finalType = getProjectFinalCalyxType(r);
    return getStatusFromCalyxType(finalType) === 'needs-mapping' && isRecentlyCreated(r.created_at);
  }).length;
  const changesDetectedCount = (rows as any[]).filter(
    (r) => Array.isArray(r.change_logs) && r.change_logs.length > 0,
  ).length;

  return (
    <Spin spinning={loading} tip="Loading generic project ID mappings…">
      <StatCards
        stats={[
          {
            label: 'Total Projects',
            value: (rows as any[]).length,
            color: '#8c8c8c',
            active: statusFilter === 'all',
            onClick: () => setStatusFilter('all'),
          },
          {
            label: 'Mapped',
            value: mappedCount,
            color: '#52c41a',
            active: statusFilter === 'mapped',
            onClick: () => setStatusFilter('mapped'),
          },
          {
            label: 'Needs Mapping',
            value: needsMappingCount,
            color: '#ff4d4f',
            active: statusFilter === 'needs-mapping',
            onClick: () => setStatusFilter('needs-mapping'),
          },
          {
            label: 'New',
            value: newCount,
            color: '#1677ff',
            active: statusFilter === 'new',
            onClick: () => setStatusFilter('new'),
          },
          {
            label: 'Registry Changes Detected',
            value: changesDetectedCount,
            color: '#fa8c16',
            active: statusFilter === 'changes-detected',
            onClick: () => setStatusFilter('changes-detected'),
            icon: <WarningOutlined />,
          },
        ]}
      />
      <div
        style={{
          margin: '0 0 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search project ID, registry…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 360 }}
        />
        <Space wrap>
          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                queryClient.invalidateQueries(['genericProjectIdMappings']);
              }}
            />
          </Tooltip>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={filteredRows}
        rowKey="id"
        size="middle"
        tableLayout="fixed"
        showSorterTooltip={false}
        onRow={(record) => {
          const isUnmapped = getStatusFromCalyxType(getProjectFinalCalyxType(record)) === 'needs-mapping';
          if (isUnmapped && isRecentlyCreated(record.created_at)) {
            return { style: { background: '#e6f4ff' } };
          }
          return {};
        }}
        pagination={{ defaultPageSize: 50, showSizeChanger: true }}
      />
      <ChangeLogsModal
        open={!!changeLogModalRecord}
        changeLogs={changeLogModalRecord?.change_logs || []}
        onClose={() => setChangeLogModalRecord(null)}
        onActionTakenAll={() => {
          const ids = (changeLogModalRecord?.change_logs || []).map((l: any) => l.id);
          if (ids.length > 0) markAllActionTaken(ids);
        }}
        actionLoading={actionLoading}
      />
    </Spin>
  );
}

// ─── Main Component with Tabs ──────────────────────────────────────────

function GenericMapper() {
  const { data: projectTypesData } = useQuery({
    queryKey: ['projectTypes'],
    queryFn: getProjectTypes,
  });

  const calyxOptions = useMemo(() => {
    const raw = Array.isArray(projectTypesData)
      ? projectTypesData
      : projectTypesData?.response || [];
    return (raw as any[]).map((pt) => ({
      value: pt.display_name,
      label: pt.display_name,
    }));
  }, [projectTypesData]);

  return (
    <div
      style={{
        padding: 24,
        background: '#f5f5f5',
        minHeight: '100vh',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: '1 1 auto' }}>
          <Title level={3} style={{ marginBottom: 2 }}>
            Generic Project Type Mapper
          </Title>
          <Text type="secondary">
            Map harmonized methodologies and project IDs to Calyx project types.
          </Text>
        </div>
        <Alert
          type="info"
          showIcon
          closable
          message="Please note that changes saved here won't appear on the platform immediately. They will be applied during the next automatic registry sync, which runs every Saturday."
          style={{ width: '50%', flexShrink: 0 }}
        />
      </div>
      <Card
        bordered={false}
        style={{ borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
      >
        <Tabs
          defaultActiveKey="registry-type"
          type="card"
          items={[
            {
              key: 'registry-type',
              label: 'Methodology Level',
              children: (
                <RegistryTypeLevelTab
                  calyxOptions={calyxOptions}
                />
              ),
            },
            {
              key: 'project-id',
              label: 'Project ID Override',
              children: (
                <ProjectIdLevelTab
                  calyxOptions={calyxOptions}
                />
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}

export default GenericMapper;
